// Why the viewer was loaded again. The page keeps a breadcrumb in
// sessionStorage (the step it is on, uptime, its own memory tally, preset and
// pixel ratio, whether it is hidden), and the next load in the same tab reads
// it and sends one GA4 event, viewer_reload, naming the cause:
//   context_loss_reload  our own reload after a lost WebGL context (the flag
//                        markOwnReload sets just before it)
//   process_killed       a reload with no clean exit: the browser loaded the
//                        tab again after its process was killed (iOS does this
//                        for a tab over its memory budget, or a background tab)
//   user_reload          a reload after a clean exit (pagehide)
// Nothing personal: the numbers, the body or mode it was on, the cause.
import { track } from './analytics.js';

const KEY = 'ss.crumb';
const EVERY_MS = 5000;

function readCrumb() {
  try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch { return null; }
}
function writeCrumb(crumb) {
  try { sessionStorage.setItem(KEY, JSON.stringify(crumb)); } catch { /* storage blocked */ }
}

let crumb = null;
let sample = () => ({});

function refresh(extra = {}) {
  if (!crumb) return;
  let state = {};
  try { state = sample() || {}; } catch { /* keep the last state */ }
  crumb = { ...crumb, ...state, ...extra, up: Math.round((Date.now() - crumb.t0) / 1000), at: Date.now() };
  writeCrumb(crumb);
}

// The previous page's breadcrumb as a cause, or null when this load is not a
// reload of a viewer page in this tab.
export function classifyLoad(prev, navType) {
  if (!prev) return null;
  if (prev.ownReload) return 'context_loss_reload';
  if (navType !== 'reload' && navType !== 'navigate') return null;
  if (prev.clean) return navType === 'reload' ? 'user_reload' : null;
  return 'process_killed';
}

// getState(): { step, preset, dpr, pr, cw, ch, texMB, textures, heapMB } for
// the breadcrumb, read every few seconds and when the page is hidden.
export function startReloadWatch(getState) {
  sample = getState;
  const prev = readCrumb();
  let navType = '';
  try { navType = performance.getEntriesByType('navigation')[0]?.type || ''; } catch { /* old browser */ }
  const cause = classifyLoad(prev, navType);
  if (cause) {
    // Hidden for a moment is only the unload itself (the page goes hidden
    // just before pagehide). A tab that was in the background says so: it
    // was hidden a while before its last breadcrumb, or that breadcrumb is
    // old (a hidden tab's timers stop).
    const hiddenS = prev.hidden && prev.hiddenAt ? Math.round(((prev.at || Date.now()) - prev.hiddenAt) / 1000) : 0;
    const gapS = prev.at ? Math.round((Date.now() - prev.at) / 1000) : 0;
    track('viewer_reload', {
      cause, nav: navType,
      step: String(prev.step || '').slice(0, 60),
      uptime_s: prev.up || 0,
      hidden: prev.hidden && (hiddenS >= 2 || gapS >= 10) ? 1 : 0,
      hidden_s: hiddenS,
      gap_s: gapS,
      tally_mb: prev.texMB || 0,
      textures: prev.textures || 0,
      heap_mb: prev.heapMB || 0,
      losses: prev.losses || 0,
      preset: prev.preset || '',
      dpr: prev.dpr || 0,
      pr: prev.pr || 0,
      canvas: prev.cw ? `${prev.cw}x${prev.ch}` : '',
      reason: prev.ownReload || '',
    });
  }
  crumb = { t0: Date.now(), clean: false, losses: 0 };
  refresh();
  setInterval(refresh, EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    refresh(document.hidden ? { hidden: true, hiddenAt: Date.now() } : { hidden: false, hiddenAt: 0 });
  });
  // A reload, a link or closing the tab ends with pagehide; a killed process
  // never gets here.
  window.addEventListener('pagehide', () => refresh({ clean: true }));
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh({ clean: false }); });
}

// Counted on the breadcrumb, so a kill soon after a lost context says so.
export function noteContextLoss() {
  if (crumb) refresh({ losses: (crumb.losses || 0) + 1 });
}

// Just before the page reloads itself.
export function markOwnReload(reason) {
  refresh({ ownReload: reason, clean: true });
}
